// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import './Panel.css';

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Panel title rendered in the panel header. */
  title?: React.ReactNode;
  /** Optional secondary description rendered beneath the title. */
  description?: React.ReactNode;
  /** Right-aligned content in the panel header (e.g. actions, filters). */
  headerActions?: React.ReactNode;
  /** Remove the default body padding — useful when rendering a full-bleed table. */
  bodyPadded?: boolean;
  children: React.ReactNode;
}



/**
 * Shared panel container used to present a titled section of content
 * (form, table, list). Renders a white card with an optional title/description
 * header followed by the children.
 */
export function Panel({
  title,
  description,
  headerActions,
  bodyPadded = false,
  className = '',
  children,
  ...rest
}: PanelProps) {
  const classes = ['fin-panel', className].filter(Boolean).join(' ');
  const bodyClasses = ['fin-panel__body', bodyPadded && 'fin-panel__body--padded']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} {...rest}>
      {(title || description || headerActions) && (
        <div className="fin-panel__header">
          <div className="fin-panel__header-text">
            {title && <h2 className="fin-panel__title">{title}</h2>}
            {description && <p className="fin-panel__description">{description}</p>}
          </div>
          {headerActions && <div className="fin-panel__actions">{headerActions}</div>}
        </div>
      )}
      <div className={bodyClasses}>{children}</div>
    </div>
  );
}
